import type {
  DraftContent,
  NormalizationProcessor,
  NormalizeConfig,
  RicosContentBlock,
  RicosInlineStyleRange,
} from '../types';
import { Version } from '../version';
import {
  addInlineStyleRanges,
  addLinkUnderlineRange,
  fixAtomicBlockText,
  fixTextColorStyle,
} from './block-processors';
import inlinePluginsRemover from './inlinePluginsRemover';
import { linkify } from './linkify';
import { removeInlineHeaderRanges } from './removeInlineHeaderRanges';
import { removeInvalidInlineStyles } from './removeInvalidInlineStyles';

// NOTE: the processor order is important
const contentStateProcessingStrategies: (
  config: NormalizeConfig
) => { version?: string; processors: NormalizationProcessor<DraftContent>[] }[] = config => {
  const { disableInlineImages, removeInvalidInlinePlugins } = config;

  const strategies = [{ version: '<3.4.7', processors: [linkify] }];
  if (disableInlineImages) {
    strategies.push({
      version: '<8.0.0',
      processors: [inlinePluginsRemover({ imagesOnly: true })],
    });
  }
  if (removeInvalidInlinePlugins) {
    strategies.push({ version: '<8.0.0', processors: [inlinePluginsRemover()] });
  }
  return strategies;
};

const blockProcessingStrategies: {
  [key: string]: { processors: NormalizationProcessor<RicosContentBlock>[] }[];
} = {
  atomic: [{ processors: [fixAtomicBlockText] }],
  unstyled: [{ processors: [removeInlineHeaderRanges, addInlineStyleRanges, fixTextColorStyle] }],
  'ordered-list-item': [
    { processors: [removeInlineHeaderRanges, addInlineStyleRanges, fixTextColorStyle] },
  ],
  'unordered-list-item': [
    { processors: [removeInlineHeaderRanges, addInlineStyleRanges, fixTextColorStyle] },
  ],
  'code-block': [
    { processors: [removeInlineHeaderRanges, addInlineStyleRanges, fixTextColorStyle] },
  ],
  'header-one': [
    { processors: [removeInlineHeaderRanges, addInlineStyleRanges, fixTextColorStyle] },
  ],
  'header-two': [
    { processors: [removeInlineHeaderRanges, addInlineStyleRanges, fixTextColorStyle] },
  ],
  'header-three': [
    { processors: [removeInlineHeaderRanges, addInlineStyleRanges, fixTextColorStyle] },
  ],
  'header-four': [
    { processors: [removeInlineHeaderRanges, addInlineStyleRanges, fixTextColorStyle] },
  ],
  'header-five': [
    { processors: [removeInlineHeaderRanges, addInlineStyleRanges, fixTextColorStyle] },
  ],
  'header-six': [
    { processors: [removeInlineHeaderRanges, addInlineStyleRanges, fixTextColorStyle] },
  ],
  blockquote: [{ processors: [removeInlineHeaderRanges, addInlineStyleRanges, fixTextColorStyle] }],
};

const entityRangeProcessingStrategies: {
  [key: string]: {
    version: string;
    processors: ((block: RicosContentBlock, range: RicosInlineStyleRange) => RicosContentBlock)[];
  }[];
} = {
  LINK: [
    {
      version: '<3.4.7',
      processors: [addLinkUnderlineRange],
    },
  ],
};

const isVersionCompatible = (strategy: { version?: string }, contentStateVersion: string) =>
  strategy.version ? Version.evaluate(contentStateVersion, strategy.version) : true;

const applyStrategies: (
  strategies: {
    version?: string;
    processors: NormalizationProcessor<DraftContent | RicosContentBlock>[];
  }[],
  processed: RicosContentBlock | DraftContent,
  version: string,
  ...args: any // eslint-disable-line @typescript-eslint/no-explicit-any
) => ReturnType<NormalizationProcessor<DraftContent | RicosContentBlock>> = (
  strategies,
  processed,
  version,
  ...args
) => {
  if (!strategies) {
    return processed;
  }

  let processedUnit = processed;
  strategies
    .filter(strategy => isVersionCompatible(strategy, version))
    .forEach(strategy => {
      strategy.processors.reduce((unit, processor) => {
        processedUnit = processor(unit, ...args);
        return processedUnit;
      }, processed);
    });
  return processedUnit;
};

export const processContentState = (contentState: DraftContent, config: NormalizeConfig) => {
  const { VERSION: contentStateVersion = '0.0.0' } = contentState;

  //process the whole state
  const processedState = applyStrategies(
    contentStateProcessingStrategies(config),
    contentState,
    contentStateVersion,
    config
  );

  const { blocks, entityMap, documentStyle } = processedState as DraftContent;

  return {
    blocks: blocks.map((block: RicosContentBlock) => {
      let processedBlock = block;

      // process block
      processedBlock = applyStrategies(
        blockProcessingStrategies[block.type],
        block,
        contentStateVersion,
        entityMap,
        config
      ) as RicosContentBlock;

      // process block entity ranges
      if (processedBlock.entityRanges) {
        processedBlock.entityRanges.forEach(range => {
          const entityType =
            entityMap && entityMap[range.key + ''] && entityMap[range.key + ''].type;
          if (entityType) {
            processedBlock = applyStrategies(
              entityRangeProcessingStrategies[entityType],
              processedBlock,
              contentStateVersion,
              range,
              entityMap,
              config
            ) as RicosContentBlock;
          }
        });
      }

      processedBlock.inlineStyleRanges = removeInvalidInlineStyles(
        processedBlock.inlineStyleRanges
      );

      return processedBlock;
    }),
    entityMap,
    documentStyle,
    VERSION: Version.lessThan(contentStateVersion, Version.currentVersion)
      ? Version.currentVersion
      : contentStateVersion,
  };
};
